import { ChevronDownIcon } from 'lucide-react';
import { isNil } from 'rambdax';
import React from 'react';
import Markdown from 'react-markdown';

import { AssetContractFormat } from '@ping/api';
import { t } from '@ping/helpers';
import { ButtonA11Y, Compact, Copyable, Loading } from '@ping/uikit';
import { DateTime, format } from '@ping/utils';

import style from './style.module.scss';

type ContractItemProps = {
  contract: any; // Poor backend api type
  isReadonly?: boolean;
  isLoading?: boolean;
};

export function ContractItem(props: ContractItemProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClamped, setIsClamped] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isNil(contentRef.current)) {
      return;
    }
    setIsClamped(contentRef.current.scrollHeight > contentRef.current.clientHeight);
  }, [props.contract?.content]);

  if (props.isLoading) {
    return (
      <div className={style['contract-card']} data-state='loading'>
        <Loading />
      </div>
    );
  }

  return (
    <div className={style['contract-card']}>
      <div className={style['contract-card-header']}>
        <div className={style['contract-card-header__row']}>
          {!isNil(props.contract?.created_by) && !props.isReadonly && (
            <span className={style['contract-created-by']}>
              <span className={style['created-by-label']}>{t('Created By')}:</span>
              <Copyable title={t('Created By')} text={props.contract.created_by}>
                <Compact text={props.contract.created_by} middle />
                <Copyable.Icon />
              </Copyable>
            </span>
          )}
          {!props.isReadonly && (
            <span className={style['contract-badge']}>
              {props.contract?.format}
              {!isNil(props.contract?.version) && ` / v${props.contract?.version}`}
            </span>
          )}
        </div>

        {props.isReadonly && (
          <>
            <span className={style['contract-created-at']}>
              <span className={style['created-at-label']}>{t('Asset ID')}:</span>{' '}
              {(props.contract?.asset_id ?? props.contract?.assetId)?.toUpperCase()}
            </span>
            <span className={style['contract-created-at']}>
              <span className={style['created-at-label']}>{t('Version')}:</span> {props.contract?.version}
            </span>
          </>
        )}

        {!isNil(props.contract?.created_at ?? props.contract?.createdAt) && (
          <span className={style['contract-created-at']}>
            <span className={style['created-at-label']}>{t('Created At')}:</span>{' '}
            {format.date(props.contract?.created_at ?? DateTime.seconds(props.contract?.createdAt))}
          </span>
        )}
      </div>

      <div ref={contentRef} className={style['contract-content']} data-expanded={isExpanded}>
        {props.contract?.format === AssetContractFormat.Markdown && <Markdown>{props.contract?.content}</Markdown>}
        {props.contract?.format !== AssetContractFormat.Markdown && <pre>{props.contract?.structured_content}</pre>}
      </div>

      {isClamped && (
        <ButtonA11Y isPrimitive className={style['expand-toggle']} onPress={() => setIsExpanded(prev => !prev)}>
          {isExpanded ? t('Show less') : t('Show more')}
          <ChevronDownIcon size={14} className={style['expand-icon']} data-expanded={isExpanded} />
        </ButtonA11Y>
      )}
    </div>
  );
}
